import React, { useState, useEffect } from "react";
import FilterBar from "../components/FilterBar";
import "react-pivottable/pivottable.css";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext";
import VerbatimFilter from "../components/VerbatimFilter";
import { exportTableToExcel } from "../utils/ExcelUtils";
import PivotSettings from "../components/pivot/PivotSettings";
import PivotLabelControls from "../components/pivot/PivotLabelControls";
import PivotTable from "../components/pivot/PivotTable";
import SampleSizeIndicator from "../components/pivot/SampleSizeIndicator";
import "./PivotPage.css";

const PivotRefactoredPage = () => {
  // States for verbatims and setup
  const { getVerbatims } = useVerbatim();
  const { labelTypes, labels } = useSetup();
  const [storedVerbatims, setStoredVerbatims] = useState([]);
  const [filteredVerbatims, setFilteredVerbatims] = useState([]);
  const [activeTab, setActiveTab] = useState("labels");

  // state for selected labels and analysis
  const [selectedRowLabel, setSelectedRowLabel] = useState("Gender");
  const [selectedColLabel, setSelectedColLabel] = useState("Age");
  const [selectedFirstLabel, setSelectedFirstLabel] = useState("");
  const [selectedSecondLabel, setSelectedSecondLabel] = useState("");
  const [excludeUnknown, setExcludeUnknown] = useState(false);
  const [crossLabelAnalysis, setCrossLabelAnalysis] = useState({ count: 0, name: "", percentage: 0 });
  
  // state for pivot data
  const [pivotData, setPivotData] = useState({});
  const [rowLabels, setRowLabels] = useState([]);
  const [colLabels, setColLabels] = useState([]);
  const [sampleSizes, setSampleSizes] = useState({ rows: {}, columns: {} });
  
  // state for sorting
  // Default sorting by first column in ascending order
  const [sortBy, setSortBy] = useState({ key: 'firstColumn', order: 'asc' });
  
  // states for display options
  const [displayOptions, setDisplayOptions] = useState({
    percentage: false,
    percentageType: "global",
    calculationType: "none",
    zScoreType: "global",
    rankType: "global",
  });
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    source: "",
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  });
  
  const [rowSentimentStates, setRowSentimentStates] = useState({
    positive: true,
    negative: true,
    mixed: true,
    neutral: true,
  });
  
  const [colSentimentStates, setColSentimentStates] = useState({
    positive: true,
    negative: true,
    mixed: true,
    neutral: true,
  });

  // Function to select sorting criteria
  const selectSortBy = (columnLabel) => {
    let order = 'asc';
    if (sortBy.key === columnLabel) order = sortBy.order === 'asc' ? 'desc' : 'asc';
    setSortBy({ key: columnLabel, order });
  };

  // Get sorted rows based on the selected sortBy key and order
  const getSortedRows = () => {
    if (!sortBy.key || !rowLabels.length) return rowLabels;
    const isKeyValid = colLabels.includes(sortBy.key);
    
    if (!isKeyValid && sortBy.key !== "firstColumn") return rowLabels;
    
    const sortableRows = [...rowLabels];
    if (sortBy.key === "firstColumn") {
      sortableRows.sort((a, b) => {
        if (sortBy.order === 'asc') return a.localeCompare(b);
        else return b.localeCompare(a);
      });
      return sortableRows;
    }
    
    sortableRows.sort((a, b) => {
      const aValue = pivotData[a]?.[sortBy.key] || 0;
      const bValue = pivotData[b]?.[sortBy.key] || 0;

      if (aValue < bValue) return sortBy.order === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortBy.order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sortableRows;
  };

  // check if sentiment is enabled for a label type
  const isSentimentEnabled = (labelType) => {
    return labelTypes[labelType]?.sentimentEnabled || false;
  };

  // Sentiment change handlers
  const handleRowSentimentChange = (state) => {
    setRowSentimentStates((prev) => ({
      ...prev,
      [state]: !prev[state],
    }));
  };

  const handleColSentimentChange = (state) => {
    setColSentimentStates((prev) => ({
      ...prev,
      [state]: !prev[state],
    }));
  };

  // calculate sample size
  const calculateSampleSize = () => {
    return filteredVerbatims.filter((verbatim) => {
      const rowLabel = verbatim.labels?.[selectedRowLabel]?.[0]?.name || "Unknown";
      const colLabel = verbatim.labels?.[selectedColLabel]?.[0]?.name || "Unknown";

      if (excludeUnknown && 
         (["Unknown", "Unknown Gender", "Unknown Age"].includes(rowLabel) ||
          ["Unknown", "Unknown Gender", "Unknown Age"].includes(colLabel))) {
        return false;
      }
      return true;
    }).length;
  };
  
  const sampleSize = calculateSampleSize();
  const isLowSampleSize = sampleSize < 30;

  // Filter change handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Load verbatims
  useEffect(() => {
    const verbatims = getVerbatims();
    setStoredVerbatims(verbatims);
  }, [getVerbatims]);

  // Apply filters
  useEffect(() => {
    const result = VerbatimFilter(storedVerbatims, filters);
    setFilteredVerbatims(result);
  }, [filters, storedVerbatims]);

  // Generate pivot table data
  const generatePivotTableData = (data, rowLabelType, colLabelType) => {
    if (!Array.isArray(data) || !rowLabelType || !colLabelType) return;

    const groupedData = {};
    const rowSet = new Set();
    const colSet = new Set();
    const localSampleSizes = { rows: {}, columns: {}, global: new Set() };

    // Fonction helper: vérifier si un label correspond aux états de sentiment actifs
    const isSentimentMatching = (sentimentStates, label) => {
      return label.state && sentimentStates[label.state.toLowerCase()];
    };

    // Fonction helper: calculer les pourcentages
    const calculatePercentagesWithSample = (data, sampleSizes, type) => {
      if (type === "row-wise") {
        Object.keys(data).forEach((row) => {
          const sampleSize = localSampleSizes.rows[row] || 1;
          Object.keys(data[row]).forEach((col) => {
            const value = data[row][col];
            data[row][col] = ((isNaN(value) ? 0 : value) / sampleSize) * 100;
          });
        });
      } else if (type === "column-wise") {
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            const sampleSize = localSampleSizes.columns[col] || 1;
            const value = data[row][col];
            data[row][col] = ((isNaN(value) ? 0 : value) / sampleSize) * 100;
          });
        });
      } else if (type === "global") {
        const globalSampleSize = localSampleSizes.global.size || 1;
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            const value = data[row][col];
            data[row][col] = ((isNaN(value) ? 0 : value) / globalSampleSize) * 100;
          });
        });
      }
    };

    // Fonction helper: calculer les Z-scores
    const calculateZScores = (data, rowSet, colSet, type) => {
      // Fonction pour calculer les Z-scores
      const calculate = (values) => {
        if (values.length === 0) return [];

        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);
        const epsilon = 1e-6;

        return values.map((val) => {
          const divisor = stdDev < epsilon ? epsilon : stdDev;
          return parseFloat(((val - mean) / divisor).toFixed(2));
        });
      };

      if (type === "global") {
        let allValues = Object.values(data).flatMap((row) => Object.values(row)).filter((val) => val !== undefined);
        allValues = allValues.map((val) => val * 100);
        const zScores = calculate(allValues);
        let index = 0;
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            data[row][col] = zScores[index++];
          });
        });
      } else if (type === "row-wise") {
        Object.keys(data).forEach((row) => {
          let values = Object.values(data[row]);
          values = values.map((val) => val * 100);
          const zScores = calculate(values);
          Object.keys(data[row]).forEach((col, index) => {
            data[row][col] = zScores[index];
          });
        });
      } else if (type === "column-wise") {
        colSet.forEach((col) => {
          let values = Array.from(rowSet).map((row) => data[row]?.[col] || 0);
          values = values.map((val) => val * 100);
          const zScores = calculate(values);
          Array.from(rowSet).forEach((row, index) => {
            data[row][col] = zScores[index];
          });
        });
      }
    };

    // Fonction helper: calculer les rangs
    const calculateRankings = (data, rowSet, colSet, type) => {
      if (type === "global") {
        const allValues = Object.values(data).flatMap((row) => Object.values(row)).sort((a, b) => b - a);
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            data[row][col] = allValues.indexOf(data[row][col]) + 1;
          });
        });
      } else if (type === "row-wise") {
        Object.keys(data).forEach((row) => {
          const values = Object.values(data[row]).sort((a, b) => b - a);
          Object.keys(data[row]).forEach((col) => {
            data[row][col] = values.indexOf(data[row][col]) + 1;
          });
        });
      } else if (type === "column-wise") {
        colSet.forEach((col) => {
          const values = Array.from(rowSet).map((row) => data[row]?.[col] || 0).sort((a, b) => b - a);
          Array.from(rowSet).forEach((row) => {
            data[row][col] = values.indexOf(data[row][col]) + 1;
          });
        });
      }
    };

    // Étape 1: Grouper et compter les occurrences
    data.forEach((verbatim) => {
      const rowLabels = (verbatim.labels?.[rowLabelType] || [])
        .filter((label) => label && label.name && 
                (!isSentimentEnabled(rowLabelType) || isSentimentMatching(rowSentimentStates, label)))
        .map((label) => label.name);

      const colLabels = (verbatim.labels?.[colLabelType] || [])
        .filter((label) => label && label.name && 
                (!isSentimentEnabled(colLabelType) || isSentimentMatching(colSentimentStates, label)))
        .map((label) => label.name);

      let finalRowLabels = rowLabels.length > 0 ? rowLabels : ["Unknown"];
      let finalColLabels = colLabels.length > 0 ? colLabels : ["Unknown"];

      if (excludeUnknown) {
        const validRowLabels = finalRowLabels.filter(
          (label) => !["Unknown", "Unknown Gender", "Unknown Age"].includes(label)
        );
        const validColLabels = finalColLabels.filter(
          (label) => !["Unknown", "Unknown Gender", "Unknown Age"].includes(label)
        );

        if (validRowLabels.length === 0 || validColLabels.length === 0) {
          return; // Skip this verbatim if it doesn't have valid labels
        }

        finalRowLabels = validRowLabels;
        finalColLabels = validColLabels;
      }

      // Ajouter les labels aux ensembles de lignes et colonnes
      finalRowLabels.forEach((label) => rowSet.add(label));
      finalColLabels.forEach((label) => colSet.add(label));

      // Grouper les données et suivre les verbatims uniques
      finalRowLabels.forEach((rowLabel) => {
        finalColLabels.forEach((colLabel) => {
          if (!groupedData[rowLabel]) groupedData[rowLabel] = {};
          if (!groupedData[rowLabel][colLabel]) groupedData[rowLabel][colLabel] = 0;

          groupedData[rowLabel][colLabel] += 1;

          // Suivre les verbatims uniques pour le calcul de la taille de l'échantillon
          if (!localSampleSizes.rows[rowLabel]) localSampleSizes.rows[rowLabel] = new Set();
          if (!localSampleSizes.columns[colLabel]) localSampleSizes.columns[colLabel] = new Set();

          localSampleSizes.rows[rowLabel].add(verbatim.id);
          localSampleSizes.columns[colLabel].add(verbatim.id);
          localSampleSizes.global.add(verbatim.id);
        });
      });
    });

    // Convertir les ensembles en comptages pour les tailles d'échantillon
    Object.keys(localSampleSizes.rows).forEach(
      (row) => (localSampleSizes.rows[row] = localSampleSizes.rows[row].size)
    );
    Object.keys(localSampleSizes.columns).forEach(
      (col) => (localSampleSizes.columns[col] = localSampleSizes.columns[col].size)
    );

    // Mettre à jour l'état avec les nouvelles tailles d'échantillon
    setSampleSizes({
      rows: localSampleSizes.rows,
      columns: localSampleSizes.columns,
    });

    // Étape 2: Appliquer le calcul de pourcentage si activé
    if (displayOptions.percentage) {
      calculatePercentagesWithSample(groupedData, sampleSizes, displayOptions.percentageType);
    }

    // Étape 3: Appliquer des calculs supplémentaires (Z-Score ou Classement) si spécifié
    if (displayOptions.calculationType === "zscore") {
      calculateZScores(groupedData, rowSet, colSet, displayOptions.zScoreType);
    } else if (displayOptions.calculationType === "rank") {
      calculateRankings(groupedData, rowSet, colSet, displayOptions.rankType);
    }

    // Finaliser et définir les sorties
    setPivotData(groupedData);
    setRowLabels(Array.from(rowSet));
    setColLabels(Array.from(colSet));
  };

  // Generate pivot table data when filters or labels change
  useEffect(() => {
    generatePivotTableData(
      filteredVerbatims,
      selectedRowLabel,
      selectedColLabel
    );
  }, [
    filteredVerbatims,
    selectedRowLabel,
    selectedColLabel,
    rowSentimentStates,
    colSentimentStates,
    excludeUnknown,
    displayOptions,
  ]);

  // Get sorted rows for the pivot table
  const sortedRows = getSortedRows();
  // Analyze cross-label verbatims
  const analyzeCrossLabelVerbatims = () => {
    if (!selectedFirstLabel || !selectedSecondLabel || !filteredVerbatims.length) return { count: 0, name: "", percentage: 0, totalVerbatimsCount: 0 };
    
    let verbatimsToAnalyze = filteredVerbatims;
    
    // Filter out verbatims with "Unknown" labels if excludeUnknown is true
    if (excludeUnknown) {
      verbatimsToAnalyze = filteredVerbatims.filter(verbatim => {
        const hasUnknownLabel = Object.values(verbatim.labels).some(labelArray =>
          labelArray.some(label =>
            label && ["Unknown", "Unknown Gender", "Unknown Age"].includes(label.name)
          )
        );
        return !hasUnknownLabel;
      });
    }
    console.log("Verbatims to analyze:", verbatimsToAnalyze);
    const crossLabelVerbatims = verbatimsToAnalyze.filter(verbatim => {
      const hasFirstLabel = Object.values(verbatim.labels).some(labelArray => 
        labelArray.some(label => label && label.name === selectedFirstLabel)
      );
      const hasSecondLabel = Object.values(verbatim.labels).some(labelArray => 
        labelArray.some(label => label && label.name === selectedSecondLabel)
      );
      
      return hasFirstLabel && hasSecondLabel;
    });
    
    const count = crossLabelVerbatims.length;
    const totalCount = verbatimsToAnalyze.length;
    const percentage = totalCount > 0 
      ? (count / totalCount) * 100
      : 0;
    
    return {
      count,
      name: `« ${selectedFirstLabel} » & « ${selectedSecondLabel} »`,
      percentage: parseFloat(percentage.toFixed(1)),
      totalVerbatimsCount: totalCount
    };
  };
  // Reset cross-label analysis
  const handleResetCrossLabelAnalysis = () => {
    setSelectedFirstLabel("");
    setSelectedSecondLabel("");
    setCrossLabelAnalysis({ count: 0, name: "", percentage: 0, totalVerbatimsCount: 0 });
  };
  // Analyze cross-label verbatims when filters or labels change
  useEffect(() => {
    const analysis = analyzeCrossLabelVerbatims();
    setCrossLabelAnalysis(analysis);
  }, [filteredVerbatims, selectedFirstLabel, selectedSecondLabel, excludeUnknown]);


  return (
    <div className="pivot-page">
      <div className="filter-bar-section">
        <FilterBar onFilterChange={handleFilterChange} />
      </div>

      <div className="pivot-section">
        <div className="tab-buttons">
          <button
            className={activeTab === "labels" ? "active" : ""}
            onClick={() => setActiveTab("labels")}
          >
            Labels
          </button>
          <button
            className={activeTab === "settings" ? "active" : ""}
            onClick={() => setActiveTab("settings")}
          >
            Settings
          </button>
        </div>

        <div className="pivot-controls">
          {activeTab === "settings" && (
            <PivotSettings 
              displayOptions={displayOptions}
              setDisplayOptions={setDisplayOptions}
              excludeUnknown={excludeUnknown}
              setExcludeUnknown={setExcludeUnknown}
            />
          )}

          {activeTab === "labels" && (
            <PivotLabelControls 
              selectedRowLabel={selectedRowLabel}
              setSelectedRowLabel={setSelectedRowLabel}
              selectedColLabel={selectedColLabel}
              setSelectedColLabel={setSelectedColLabel}
              selectedFirstLabel={selectedFirstLabel}
              setSelectedFirstLabel={setSelectedFirstLabel}
              selectedSecondLabel={selectedSecondLabel}
              setSelectedSecondLabel={setSelectedSecondLabel}
              labelTypes={labelTypes}
              labels={labels}
              isSentimentEnabled={isSentimentEnabled}
              rowSentimentStates={rowSentimentStates}
              colSentimentStates={colSentimentStates}
              handleRowSentimentChange={handleRowSentimentChange}
              handleColSentimentChange={handleColSentimentChange}
              setSortBy={setSortBy}
              handleResetCrossLabelAnalysis={handleResetCrossLabelAnalysis}
            />
          )}
        </div>

        <SampleSizeIndicator 
          sampleSize={sampleSize} 
          isLowSampleSize={isLowSampleSize}
          crossLabelAnalysis={crossLabelAnalysis}
          onExport={() => exportTableToExcel(
            pivotData,
            rowLabels,
            colLabels,
            selectedRowLabel,
            selectedColLabel,
            "PivotTable.xlsx",
            displayOptions.percentage,
            sampleSize,
            displayOptions.calculationType === "none" 
              ? displayOptions.percentage ? "percentage" : "raw" 
              : displayOptions.calculationType
          )}
        />

        {filteredVerbatims.length > 0 && (
          <PivotTable 
            pivotData={pivotData}
            sortedRows={sortedRows}
            colLabels={colLabels}
            displayOptions={displayOptions}
            sampleSizes={sampleSizes}
            selectedRowLabel={selectedRowLabel}
            selectedColLabel={selectedColLabel}
            sortBy={sortBy}
            selectSortBy={selectSortBy}
          />
        )}
      </div>
    </div>
  );
};

export default PivotRefactoredPage;
